// © Thibault Sabathier, 2026. Tous droits réservés.
// frontend/src/views/jobs/searches/ResponsesModal.js
//
// « Je peux aider » : propositions reçues sur UNE de mes demandes de mise en relation. Le demandeur voit
// l'aidant, son message (texte brut) et décide : accepter (une conversation s'ouvre), refuser (discret : l'aidant
// n'est pas prévenu) ou, après une mise en relation acceptée, confirmer qu'elle a abouti.
//
// Les actions affichées sont celles que le serveur autorise (`canAccept`, `canDecline`, `canMarkDone`). Un clic
// verrouille l'écran entier (ref partagée) : pas de double envoi, pas de deux actions simultanées.

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CButton,
  CBadge,
  CAlert,
  CSpinner,
} from '@coreui/react'
import Avatar from '../../../components/shared/Avatar'
import SearchPager from './SearchPager'
import { formatDateI18n } from '../../../utils/i18nFormatters'
import {
  fetchSearchResponses,
  acceptResponse,
  declineResponse,
  completeResponse,
} from '../../../utils/memberSearchesApi'
import {
  responseStatusColor,
  describeMember,
  orderForAuthor,
  conversationIdOf,
  runResponseAction,
  requestOpenConversation,
} from '../../../utils/memberSearchResponses'
import './searches.scss'

/**
 * @param {{search: (object|null), onClose: Function, onChanged: Function}} props
 *   search = ma demande ; null = modale fermée. onChanged : appelé après une action réussie (compteurs de la liste).
 */
const ResponsesModal = ({ search, onClose, onChanged }) => {
  const { t, i18n } = useTranslation()
  const [page, setPage] = useState(1)
  const [data, setData] = useState({ responses: [], pagination: null })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null) // { color, text }
  const [busyId, setBusyId] = useState(null)
  const [confirmingDecline, setConfirmingDecline] = useState(null)
  const seq = useRef(0)
  const lock = useRef(false)

  const load = useCallback(
    async (id, nextPage) => {
      const mine = ++seq.current
      setLoading(true)
      setError(null)
      try {
        const res = await fetchSearchResponses(id, { page: nextPage })
        if (mine !== seq.current) return
        setData({ responses: res.responses || [], pagination: res.pagination || null })
      } catch (err) {
        if (mine !== seq.current) return
        setError(t('searches:responses.loading_error'))
        setData({ responses: [], pagination: null })
      } finally {
        if (mine === seq.current) setLoading(false)
      }
    },
    [t],
  )

  useEffect(() => {
    if (!search) return
    setPage(1)
    setNotice(null)
    setConfirmingDecline(null)
    load(search.id, 1)
  }, [search, load])

  const goTo = (next) => {
    setPage(next)
    load(search.id, next)
  }

  const act = async (response, call, successKey) => {
    setNotice(null)
    setBusyId(response.id)
    const outcome = await runResponseAction({ lock, call: () => call(response.id), successKey })
    if (outcome.skipped) return
    setBusyId(null)
    setConfirmingDecline(null)
    setNotice({ color: outcome.notice.color, text: t(`searches:${outcome.notice.key}`) })
    if (outcome.ok) {
      // L'acceptation ouvre la conversation créée par le serveur (conversationId de la réponse).
      if (outcome.result && outcome.result.conversationId)
        requestOpenConversation(outcome.result.conversationId)
      if (onChanged) onChanged()
    }
    if (outcome.reload) await load(search.id, page)
  }

  const total = data.pagination ? data.pagination.total : data.responses.length
  const items = orderForAuthor(data.responses)

  return (
    <CModal visible={Boolean(search)} onClose={onClose} alignment="center" size="lg" scrollable>
      <CModalHeader>
        <CModalTitle>{t('searches:responses.modal_title')}</CModalTitle>
      </CModalHeader>
      <CModalBody>
        {search && <p className="fw-semibold mb-1">{search.title}</p>}
        <p className="ms-note">{t('searches:responses.modal_intro')}</p>

        {notice && (
          <CAlert color={notice.color} dismissible onClose={() => setNotice(null)}>
            {notice.text}
          </CAlert>
        )}
        {error && (
          <CAlert
            color="danger"
            className="d-flex align-items-center justify-content-between gap-2"
          >
            <span>{error}</span>
            <CButton size="sm" color="light" onClick={() => search && load(search.id, page)}>
              {t('searches:responses.retry')}
            </CButton>
          </CAlert>
        )}

        {loading ? (
          <div className="text-center py-4">
            <CSpinner />
          </div>
        ) : error ? null : items.length === 0 ? (
          <div className="ms-empty">{t('searches:responses.empty')}</div>
        ) : (
          <>
            <p className="small text-medium-emphasis mb-2">
              {t('searches:responses.total', { count: total })}
            </p>
            <ul className="ms-responses">
              {items.map((r) => {
                const helper = describeMember(r.helper)
                const conversationId = conversationIdOf(r)
                const busy = busyId === r.id
                return (
                  <li key={r.id} className="ms-response" data-response-id={r.id}>
                    <div className="ms-response-head">
                      {helper.deleted ? (
                        <span className="text-medium-emphasis">
                          {t('searches:responses.member_deleted')}
                        </span>
                      ) : (
                        <Link
                          to={`/members/${helper.profileId}`}
                          className="ms-author-link"
                          onClick={onClose}
                        >
                          <Avatar user={r.helper} size={32} />
                          <span className="fw-semibold">{helper.name}</span>
                        </Link>
                      )}
                      <CBadge color={responseStatusColor(r.status)}>
                        {t(`searches:responses.status.${r.status}`, { defaultValue: r.status })}
                      </CBadge>
                    </div>
                    {helper.subtitle && (
                      <div className="small text-medium-emphasis">{helper.subtitle}</div>
                    )}
                    <p className="ms-response-text">{r.message}</p>
                    <div className="small text-medium-emphasis">
                      {t('searches:responses.sent_on', {
                        date: formatDateI18n(r.createdAt, 'P', i18n.language),
                      })}
                    </div>

                    <div className="ms-actions">
                      {busy && <CSpinner size="sm" />}
                      {conversationId && (
                        <CButton
                          size="sm"
                          color="primary"
                          variant="outline"
                          onClick={() => requestOpenConversation(conversationId)}
                        >
                          {t('searches:responses.open_conversation')}
                        </CButton>
                      )}
                      {r.canAccept && (
                        <CButton
                          size="sm"
                          color="primary"
                          disabled={busyId !== null}
                          onClick={() => act(r, acceptResponse, 'responses.notice.accepted')}
                        >
                          {t('searches:responses.accept')}
                        </CButton>
                      )}
                      {r.canDecline &&
                        (confirmingDecline === r.id ? (
                          <>
                            <span className="small text-medium-emphasis">
                              {t('searches:responses.decline_hint')}
                            </span>
                            <CButton
                              size="sm"
                              color="danger"
                              disabled={busyId !== null}
                              onClick={() => act(r, declineResponse, 'responses.notice.declined')}
                            >
                              {t('searches:responses.decline_confirm')}
                            </CButton>
                            <CButton
                              size="sm"
                              color="light"
                              disabled={busyId !== null}
                              onClick={() => setConfirmingDecline(null)}
                            >
                              {t('searches:actions.cancel')}
                            </CButton>
                          </>
                        ) : (
                          <CButton
                            size="sm"
                            color="danger"
                            variant="ghost"
                            disabled={busyId !== null}
                            onClick={() => setConfirmingDecline(r.id)}
                          >
                            {t('searches:responses.decline')}
                          </CButton>
                        ))}
                      {r.canMarkDone && (
                        <CButton
                          size="sm"
                          color="success"
                          variant="outline"
                          disabled={busyId !== null}
                          onClick={() => act(r, completeResponse, 'responses.notice.done')}
                        >
                          {t('searches:responses.mark_done')}
                        </CButton>
                      )}
                    </div>
                  </li>
                )
              })}
            </ul>
            <SearchPager pagination={data.pagination} onPage={goTo} />
          </>
        )}
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" variant="outline" onClick={onClose}>
          {t('searches:actions.back')}
        </CButton>
      </CModalFooter>
    </CModal>
  )
}

export default ResponsesModal
